import React, { useState, useEffect } from 'react';
import { Tag, Truck, X, Gift, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 40 es el tamaño interno de los perfumes que en la tienda se muestran como 35ml
const sizeLabel = (ml) => (ml === 40 ? 35 : ml);
const isBundle = (promo) => promo.promo_type === 'bundle_gift';
// Etiqueta corta de la promo: "3x2" para NxM, "5x3" para regalos (compra N, te llevas N+2)
const promoBadge = (promo) =>
  isBundle(promo)
    ? `${promo.buy_quantity + (promo.free_pocket_qty || 0) + (promo.gift_mini_qty || 0)}x${promo.buy_quantity}`
    : `${promo.buy_quantity}x${promo.pay_quantity}`;
const promoSummary = (promo) =>
  isBundle(promo)
    ? `Compra ${promo.buy_quantity} de ${promo.buy_size_ml}ml y llévate uno de ${sizeLabel(promo.free_pocket_size_ml)}ml y uno de 10ml gratis`
    : `Compra ${promo.buy_quantity}, paga ${promo.pay_quantity}`;

const PromoBanner = ({ onHeightChange }) => {
  const [promos, setPromos] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showMobileModal, setShowMobileModal] = useState(false);

  useEffect(() => {
    fetchActivePromos();
  }, []);

  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Notify parent of height change (only for desktop banner, mobile uses floating button)
    if (onHeightChange) {
      const height = (!loading && promos.length > 0 && !dismissed && !isMobile) ? 48 : 0;
      onHeightChange(height);
    }
  }, [loading, promos, dismissed, onHeightChange, isMobile]);

  const fetchActivePromos = async () => {
    try {
      const res = await fetch(`${API_URL}/api/promos/active`);
      if (res.ok) {
        const data = await res.json();
        setPromos(data.promos || []);
      }
    } catch (error) {
      console.error('Error fetching promos:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || promos.length === 0 || dismissed) return null;

  return (
    <>
      {/* Desktop Banner - Hidden on mobile */}
      <div className="hidden sm:block fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white py-2.5 px-4 overflow-hidden">
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              rgba(255,255,255,0.1) 10px,
              rgba(255,255,255,0.1) 20px
            )`
          }} />
        </div>

        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 relative z-10">
          {promos.map((promo, idx) => (
            <div key={promo.id} className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1">
                <Tag size={14} className="animate-pulse" />
                <span className="font-bold">{promoBadge(promo)}</span>
              </div>
              <div className="text-sm">
                <span className="font-medium">
                  {promoSummary(promo)}
                </span>
                {!isBundle(promo) && (
                  <span className="text-white/80 ml-1">
                    ({sizeLabel(promo.applies_to_size)}ml)
                  </span>
                )}
              </div>
              {idx < promos.length - 1 && (
                <span className="hidden sm:block text-white/40 mx-1">|</span>
              )}
            </div>
          ))}
          
          {/* Shipping info (solo para promos NxM; las de regalos no cambian el envío) */}
          {promos[0] && promos.some((p) => !isBundle(p)) && (
            <div className="flex items-center gap-1.5 text-sm">
              <Truck size={14} />
              <span>Envío GRATIS a {promos[0].shipping_free_city}</span>
            </div>
          )}
        </div>

        {/* Dismiss button */}
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-1/2 right-2 -translate-y-1/2 p-1 hover:bg-white/20 rounded-full transition-colors"
          aria-label="Cerrar banner"
        >
          <X size={16} />
        </button>
      </div>

      {/* Mobile Floating Button */}
      <button
        onClick={() => setShowMobileModal(true)}
        className="sm:hidden fixed bottom-20 left-4 z-[60] group"
        aria-label="Ver promociones"
      >
        <div className="relative">
          {/* Pulsing ring animation */}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-cobalt to-brand-magenta rounded-full animate-ping opacity-30" />
          
          {/* Main button */}
          <div className="relative flex items-center gap-2 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white px-4 py-3 rounded-full shadow-lg shadow-orange-500/30">
            <Gift size={18} className="animate-bounce" />
            <span className="font-bold text-sm">PROMOS</span>
            <Sparkles size={14} className="animate-pulse" />
          </div>
        </div>
      </button>

      {/* Mobile Modal */}
      {showMobileModal && (
        <div className="sm:hidden fixed inset-0 z-[70] flex items-end justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowMobileModal(false)}
          />
          
          {/* Modal Content */}
          <div className="relative w-full max-h-[80vh] bg-white rounded-t-3xl overflow-hidden animate-slide-up">
            {/* Header with gradient */}
            <div className="relative bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-6 pb-8">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Gift size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Nuestras Promos</h3>
                    <p className="text-white/80 text-sm">Promociones activas</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowMobileModal(false)}
                  className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"
                >
                  <X size={20} className="text-white" />
                </button>
              </div>
            </div>

            {/* Promo Cards */}
            <div className="p-4 space-y-4 overflow-y-auto max-h-[50vh]">
              {promos.map((promo) => (
                <div
                  key={promo.id}
                  className="bg-brand-bone border border-brand-black/10 rounded-2xl p-4 relative overflow-hidden"
                >
                  {/* Decorative corner */}
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-brand-cobalt/15 to-transparent rounded-bl-full" />

                  <div className="flex items-start gap-4">
                    {/* Big number badge */}
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-brand-cobalt to-brand-magenta rounded-xl flex flex-col items-center justify-center">
                      <span className="text-2xl font-black text-white">{promoBadge(promo)}</span>
                    </div>

                    <div className="flex-1">
                      <h4 className="font-bold text-brand-black text-lg">{promo.name}</h4>
                      {isBundle(promo) ? (
                        <p className="text-brand-black/60 text-sm mt-1">{promoSummary(promo)}</p>
                      ) : (
                        <p className="text-brand-black/60 text-sm mt-1">
                          Compra <span className="text-brand-cobalt font-bold">{promo.buy_quantity}</span> y paga solo <span className="text-brand-magenta font-bold">{promo.pay_quantity}</span>
                        </p>
                      )}
                      <div className="mt-2 inline-flex items-center gap-1.5 bg-brand-cobalt/10 text-brand-cobalt px-3 py-1 rounded-full text-sm">
                        <Tag size={12} />
                        <span>Presentación {sizeLabel(isBundle(promo) ? promo.buy_size_ml : promo.applies_to_size)}ml</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Shipping info card */}
              {promos[0] && promos.some((p) => !isBundle(p)) && (
                <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-2xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                      <Truck size={24} className="text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-green-600">Envío GRATIS</h4>
                      <p className="text-brand-black/60 text-sm">
                        A todo el estado de {promos[0].shipping_free_city}
                      </p>
                      <p className="text-brand-black/40 text-xs mt-1">
                        Resto del país: $99
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom CTA */}
            <div className="p-4 border-t border-brand-black/10">
              <button
                onClick={() => setShowMobileModal(false)}
                className="w-full py-3 bg-gradient-to-r from-brand-cobalt to-brand-magenta text-white font-bold rounded-xl"
              >
                ¡Aprovechar ofertas!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animation keyframes */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default PromoBanner;
